import { z } from "zod";
import {
  type CreateServiceRequest,
  type SendServiceRequestQuote,
  type SendDirectServiceRequestQuote,
  type SendDirectServiceRequestQuoteWithChannel,
  type SendServiceRequestQuoteResponse,
  type SendServiceRequestQuoteWithChannel,
  type SendServiceRequestQuoteWithChannelResponse,
  type ServiceRequestQuotePublicResponse,
  type ServiceRequestResponse,
  type ServiceRequestListResponse,
  type ListServiceRequestsQuery,
  type UpdateServiceRequest,
  serviceRequestResponseSchema,
  serviceRequestListResponseSchema,
  sendServiceRequestQuoteResponseSchema,
  sendServiceRequestQuoteWithChannelResponseSchema,
  serviceRequestQuotePublicResponseSchema,
} from "@openings/api-contract";
import { BaseHttpClient, baseOk, type ApiOk } from "./base";

const serviceRequestResponse = baseOk(serviceRequestResponseSchema);
const serviceRequestListResponse = baseOk(serviceRequestListResponseSchema);
const sendQuoteResponse = baseOk(sendServiceRequestQuoteResponseSchema);
const sendQuoteWithChannelResponse = baseOk(
  sendServiceRequestQuoteWithChannelResponseSchema,
);
const quotePublicResponse = baseOk(serviceRequestQuotePublicResponseSchema);

/** Response schema for the public service-request creation endpoint. */
const publicServiceRequestResponse = baseOk(
  z.object({ serviceRequestId: z.string() }),
);

export type CreateServiceRequestResult = ApiOk<ServiceRequestResponse>;
export type ListServiceRequestsResult = ApiOk<ServiceRequestListResponse>;
export type SendServiceRequestQuoteResult =
  ApiOk<SendServiceRequestQuoteResponse>;
export type SendServiceRequestQuoteWithChannelResult =
  ApiOk<SendServiceRequestQuoteWithChannelResponse>;
export type GetServiceRequestQuotePublicResult =
  ApiOk<ServiceRequestQuotePublicResponse>;

/** Input accepted by the public (unauthenticated) service-request endpoint. */
export interface CreateServiceRequestPublicInput {
  userId?: string;
  teamMemberId?: string;
  scheduleId?: string;
  salonId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  note?: string;
  userPhotos?: string[];
  referencePhotos?: string[];
  serviceIds?: string[];
  serviceTitles?: string[];
}

export type CreateServiceRequestPublicResult = ApiOk<{
  serviceRequestId: string;
}>;

export class ServiceRequestsClient extends BaseHttpClient {
  create(input: CreateServiceRequest): Promise<CreateServiceRequestResult> {
    return this.post("/v1/service-requests", input, serviceRequestResponse);
  }

  /** Authenticated endpoint: persist + email a quote link for a request. */
  sendQuote(
    input: SendServiceRequestQuote,
  ): Promise<SendServiceRequestQuoteResult> {
    return this.post(
      `/v1/service-requests/${encodeURIComponent(input.requestId)}/quote/exact`,
      input,
      sendQuoteResponse,
    );
  }

  /**
   * Authenticated endpoint: create a new ServiceRequest (status=QUOTED) and
   * email the quote in one step. Used for staff-initiated quotes where the
   * customer hasn't submitted a request themselves.
   */
  sendDirectQuote(
    input: SendDirectServiceRequestQuote,
  ): Promise<SendServiceRequestQuoteResult> {
    return this.post(
      `/v1/service-requests/direct-quote/exact`,
      input,
      sendQuoteResponse,
    );
  }

  /**
   * Issue a quote on the operator's chosen channel (Oct 2026). Calls only the
   * `/channel` path, with no fallback: an older API answers 404 before any
   * mutation instead of stripping `channel` and emailing a quote the
   * operator asked to text. Strict envelope: a response without `channel`
   * and `sent` fails closed instead of falling back to the legacy shape.
   */
  sendQuoteWithChannel(
    input: SendServiceRequestQuoteWithChannel,
  ): Promise<SendServiceRequestQuoteWithChannelResult> {
    return this.postStrict(
      `/v1/service-requests/${encodeURIComponent(input.requestId)}/quote/channel`,
      input,
      sendQuoteWithChannelResponse,
    );
  }

  /** Direct quote on the operator's chosen channel; see `sendQuoteWithChannel`. */
  sendDirectQuoteWithChannel(
    input: SendDirectServiceRequestQuoteWithChannel,
  ): Promise<SendServiceRequestQuoteWithChannelResult> {
    return this.postStrict(
      `/v1/service-requests/direct-quote/channel`,
      input,
      sendQuoteWithChannelResponse,
    );
  }

  /** Public endpoint: fetch quote details by public id (no PII). */
  getQuotePublic(
    quotePublicId: string,
  ): Promise<GetServiceRequestQuotePublicResult> {
    return this.get(
      `/v1/service-requests/quotes/${encodeURIComponent(quotePublicId)}`,
      quotePublicResponse,
    );
  }

  /** Create a service request without authentication (public booking flow). */
  createPublic(
    input: CreateServiceRequestPublicInput,
  ): Promise<CreateServiceRequestPublicResult> {
    return this.post(
      "/v1/service-requests/public",
      input,
      publicServiceRequestResponse,
    );
  }

  /**
   * @deprecated Prefer server-side/admin service request queries; avoid introducing new direct list usage from clients.
   */
  list(params: ListServiceRequestsQuery): Promise<ListServiceRequestsResult> {
    const qs = new URLSearchParams({
      userId: params.userId,
      salonId: params.salonId,
    });
    if (params.status) qs.set("status", params.status);

    return this.get(
      `/v1/service-requests?${qs.toString()}`,
      serviceRequestListResponse,
    );
  }

  /**
   * @deprecated Prefer fetching service requests through domain-specific aggregate endpoints instead of direct ID reads.
   */
  getById(requestId: string): Promise<ApiOk<ServiceRequestResponse>> {
    return this.get(
      `/v1/service-requests/${encodeURIComponent(requestId)}`,
      serviceRequestResponse,
    );
  }

  /**
   * Update a request — the dashboard closes one through this. Going through
   * the API (not a direct write) lets it read the assigned member's request
   * alert and re-sync their app-icon badge.
   */
  update(input: UpdateServiceRequest): Promise<ApiOk<ServiceRequestResponse>> {
    return this.put(
      `/v1/service-requests/${encodeURIComponent(input.requestId)}`,
      input,
      serviceRequestResponse,
    );
  }

  /** Resend an existing quote email without modifying the quote. */
  resendQuote(requestId: string): Promise<ApiOk<{ success: boolean }>> {
    return this.post(
      `/v1/service-requests/${encodeURIComponent(requestId)}/quote/resend`,
      {},
      baseOk(z.object({ success: z.boolean() })),
    );
  }
}
